import { useQueries } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { sumStats, todayIso, yearRange } from '../../utils/dateBuckets';
import { Card } from '../ui/Card';
import { Spinner } from '../ui/Spinner';
import { ChartTooltip, chartColors } from './ChartTooltip';
import { TrendStat } from './TrendStat';

const YEARS_BACK = 5;

export function YearTrendView() {
  const { currentSiteId } = useAuth();
  const thisYear = Number(todayIso().slice(0, 4));
  const years = Array.from({ length: YEARS_BACK }, (_, i) => thisYear - YEARS_BACK + 1 + i);

  const queries = useQueries({
    queries: years.map((year) => {
      const range = yearRange(year);
      return {
        queryKey: ['daily-stats', currentSiteId, range.from, range.to],
        queryFn: () => api.dailyStats(currentSiteId!, range.from, range.to),
        enabled: !!currentSiteId,
      };
    }),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const chartData = years.map((year, i) => ({ year: String(year), ...sumStats(queries[i]!.data?.days ?? []) }));

  const currentTotals = chartData[chartData.length - 1]!;
  const prevTotals = chartData[chartData.length - 2]!;

  return (
    <div className="space-y-6">
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <TrendStat label={`Besucher ${thisYear}`} value={currentTotals.visitors} previousValue={prevTotals.visitors} />
            <TrendStat label={`Sessions ${thisYear}`} value={currentTotals.sessions} previousValue={prevTotals.sessions} />
            <TrendStat label={`Conversions ${thisYear}`} value={currentTotals.conversions} previousValue={prevTotals.conversions} />
            <TrendStat label={`Seitenaufrufe ${thisYear}`} value={currentTotals.pageViews} previousValue={prevTotals.pageViews} />
          </div>

          <Card title={`Jahresverlauf ${years[0]} – ${years[years.length - 1]}`}>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="year" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={12} allowDecimals={false} tickLine={false} axisLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12, color: '#94a3b8' }} />
                <Bar dataKey="visitors" name="Besucher" fill={chartColors.visitors} radius={[4, 4, 0, 0]} />
                <Bar dataKey="conversions" name="Conversions" fill={chartColors.conversions} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </>
      )}
    </div>
  );
}
